/**
 * ViewModeContext
 * Manages the admin view mode toggle (personal vs school-wide view)
 * Persists across pages and in localStorage
 */

import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

type ViewMode = 'personal' | 'school';

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

export const ViewModeProvider = ({ children }: { children: ReactNode }) => {
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('adminViewMode');
    return (saved === 'school' ? 'school' : 'personal') as ViewMode;
  });

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    localStorage.setItem('adminViewMode', mode);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'personal' ? 'school' : 'personal');
  };

  return (
    <ViewModeContext.Provider value={{ viewMode, setViewMode, toggleViewMode }}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};
